//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { IUrlRouterProvider } from 'angular-ui-router';

import { hasPermissionForTask } from '@services/permissions';
import { TWITTER_SCHEDULE_TASK_NAME } from '@twitter/redux/schedule/twitterScheduleModel';

import {
  IPartialState,
  NavGroup,
  StateFactory,
  componentNameToTemplate,
  hasAllFeatures,
  hasFeature,
  lazyLoadModule,
} from 'app/initialization/routing/stateHelpers';

// Sets default values for state data
function TwitterStateFactory(state: IPartialState) {
  const accessFn = state.data?.hasAccess ?? (() => true);
  const data = {
    title: 'X Account',
    navGroup: NavGroup.PUBLISHING,
    ...state.data,
    hasAccess: () =>
      hasAllFeatures(FeatureNames.Social, FeatureNames.SocialDashboards) &&
      accessFn(),
  };

  return StateFactory({
    ...state,
    data,
  });
}

export function routingRules(urlRouterProvider: IUrlRouterProvider) {
  urlRouterProvider.when(
    '/twitter-v2/schedule',
    '/twitter-v2/schedule/timeline',
  );
  urlRouterProvider.when('/twitter-v2/drafts', '/schedule/drafts');
}

export const states = {
  twitterV2: TwitterStateFactory({
    url: '/twitter-v2',
    template: componentNameToTemplate('twitterPageV2'),
    redirectTo: 'twitterV2.summary',
    resolve: {
      ...lazyLoadModule(
        'curalate.twitterV2',
        () => import('@twitterV2/TwitterV2App'),
      ),
    },
  }),
  'twitterV2.summary': TwitterStateFactory({
    url: '/summary',
    views: {
      'twitter-header': {
        template: `<twitter-summary-header></twitter-summary-header>`,
      },
      'twitter-content': {
        template: componentNameToTemplate('twitterSummary'),
      },
    },
  }),
  'twitterV2.engagement': TwitterStateFactory({
    url: '/engagement',
    views: {
      'twitter-header': {
        template: `<twitter-header title="Organic Post Engagement"></twitter-header>`,
      },
      'twitter-content': {
        template: componentNameToTemplate('twitterEngagement'),
      },
    },
  }),
  'twitterV2.audience': TwitterStateFactory({
    url: '/audience',
    views: {
      'twitter-header': {
        template: `<twitter-header title="Audience"></twitter-header>`,
      },
      'twitter-content': {
        template: componentNameToTemplate('twitterAudience'),
      },
    },
  }),
  // TODO: Move all of the existing schedule routes/pages to our twitter-v2 app once shipped (DEV-61594)
  'twitterV2.schedule': TwitterStateFactory({
    url: '/schedule',
    views: {
      'twitter-header': {
        template: `<twitter-header title="Schedule"></twitter-header>`,
      },
      'twitter-content': {
        template:
          '<div ui-view="twitter-sub" class="flex-tall cu-h-100"></div>',
      },
    },
  }),
  'twitterV2.schedule.timeline': TwitterStateFactory({
    url: '/timeline',
    views: {
      'twitter-sub': {
        template: componentNameToTemplate('twitterSchedule'),
      },
    },
    resolve: {
      ...lazyLoadModule(
        'curalate.twitter',
        () => import('@twitter/components/schedule/TwitterSchedule'),
      ),
    },
  }),
  'twitterV2.schedule.postDetails': TwitterStateFactory({
    url: '/post-details?:scheduledPostId&:copiedScheduledPostId&:copiedDraftId&:postTimeMs&:targetCuid&:draftPostId&:postType',
    views: {
      'twitter-sub': {
        template: componentNameToTemplate('twitterPostDetailsPage'),
      },
    },
    data: {
      hasAccess: () => hasPermissionForTask(TWITTER_SCHEDULE_TASK_NAME),
    },
    resolve: {
      ...lazyLoadModule(
        'curalate.twitter',
        () => import('@twitter/components/post-details/TwitterPostDetailsPage'),
      ),
    },
  }),
};
